import './referrer.css';
import { restrictCommands } from '../commands';
import { applyBranding } from '../ris';
import { risSession, sessionWatermark } from '../ris/session';
import type { ViewerShell } from '../shell';
import { referrerAllowsCommand, watermarkBackground, watermarkText } from './policy';

/**
 * The referrer layout: what the results portal opens for a referring doctor or convenio user. The
 * reading workspace (src/main.ts installs it from referrerModuleList) under a watermark naming who
 * opened it, when and for which org, without export, print, share, the assistant or ris writes
 * (src/referrer/policy.ts). The ticket carries only viewer:open, so ris refuses those writes anyway.
 */

export const REFERRER_TEXT = {
  print: 'Estas imágenes son solo para consulta en pantalla y no se pueden imprimir.',
} as const;

declare global {
  interface Window {
    /** Test hook: the watermark text on screen, or null before install. */
    __referrerViewer?: { watermark: () => string | null };
  }
}

let watermark: HTMLElement | null = null;

function blockSaveImage(event: Event): void {
  event.preventDefault();
}

/**
 * Before any workspace module builds: the layout flag the CSS keys on, the command gate (so the
 * toolbar and menus are built without the refused entries) and no browser "save image" menu anywhere.
 */
export function prepare(): void {
  document.documentElement.dataset.layout = 'referrer';
  restrictCommands(referrerAllowsCommand);
  document.addEventListener('contextmenu', blockSaveImage, true);
  const notice = document.createElement('div');
  notice.className = 'referrer-print-notice';
  notice.textContent = REFERRER_TEXT.print;
  document.body.appendChild(notice);
}

function buildWatermark(text: string): HTMLElement {
  const layer = document.createElement('div');
  layer.className = 'referrer-watermark';
  layer.setAttribute('aria-hidden', 'true');
  layer.dataset.watermark = text;
  layer.style.backgroundImage = watermarkBackground(text);
  return layer;
}

/** Keeps the layer over the image area: re-attached if anything ever detaches it. */
function keepAttached(host: HTMLElement, layer: HTMLElement): void {
  const ensure = (): void => {
    if (layer.parentElement !== host) host.appendChild(layer);
  };
  ensure();
  new MutationObserver(ensure).observe(host, { childList: true });
}

/** Installed where the ris module would be (after the toolbar): org branding and the watermark. */
export function install(shell: ViewerShell): void {
  const session = risSession();
  if (session) void applyBranding(shell, session);
  const text = watermarkText(sessionWatermark(), session?.organization.name);
  const host = shell.regions.grid.parentElement ?? shell.regions.grid;
  watermark = buildWatermark(text);
  keepAttached(host, watermark);
  window.__referrerViewer = { watermark: () => watermark?.dataset.watermark ?? null };
}
